import { useTranslation } from 'next-i18next'

import Schema from './Schema'
import { Question, Answer } from './Schema.types'

interface Props {
  questions: Question[]
  subject: string
  name: string
  educationalLevel: 'beginner' | 'intermediate' | 'advanced'
  typicalAgeRange: `${number}-${number}`
}

const QuizSchema = ({ questions, subject, name, educationalLevel, typicalAgeRange }: Props): React.ReactElement => {
  const { t } = useTranslation('quizSchema')

  const formatAnswer = (answer: Answer): any => {
    const defaultComment = answer.isCorrect ? t('correctAnswer') : t('incorrectAnswer')
    return {
      '@type': 'Answer',
      position: answer.position,
      text: answer.text,
      encodingFormat: 'text/markdown',
      comment: {
        '@type': 'Comment',
        text: answer.comment ?? defaultComment,
      },
      ...(answer.isCorrect
        ? {
            answerExplanation: {
              '@type': 'Comment',
              text: answer.explanation ?? t('answerExplanation', { answer: answer.text }),
            },
          }
        : undefined),
    }
  }

  const formatQuestion = (question: Question): any => {
    return {
      '@type': 'Question',
      name: 'Quiz yourself',
      typicalAgeRange,
      text: question.text,
      comment: {
        '@type': 'Comment',
        text: question.comment ?? question.text,
      },
      educationalAlignment: [
        {
          '@type': 'AlignmentObject',
          alignmentType: 'educationalSubject',
          targetName: 'Mathematics',
        },
        {
          '@type': 'AlignmentObject',
          alignmentType: 'educationalSubject',
          targetName: subject,
        },
      ],
      encodingFormat: 'text/markdown',
      eduQuestionType: 'Multiple choice',
      learningResourceType: 'Practice problem',
      suggestedAnswer: question.answers.filter((answer) => !answer.isCorrect).map(formatAnswer),
      acceptedAnswer: question.answers.filter((answer) => answer.isCorrect).map(formatAnswer)[0],
    }
  }

  const schemaObject = {
    '@context': 'https://schema.org/',
    '@type': 'Quiz',
    name,
    about: {
      '@type': 'Thing',
      name: subject,
    },
    educationalLevel,
    assesses: t('mathematics'),
    hasPart: questions.length === 1 ? formatQuestion(questions[0]) : questions.map(formatQuestion),
  }
  return <Schema schemaObject={schemaObject} />
}

export default QuizSchema
